import React, { useEffect, useState } from 'react';
import { useDragLayer, XYCoord } from 'react-dnd';
import { Element as ElementType } from '../types';

// Helper function to get default color by element type
function getDefaultColorByType(type: ElementType['type']): string {
  switch (type) {
    case 'water': return '#3b82f6';
    case 'fire': return '#ef4444';
    case 'dirt': return '#8B6F4E';
    case 'air': return '#e2e8f0';
    case 'discovery': return '#8b5cf6';
    default: return '#8b5cf6';
  }
}

// Helper function to determine if text should be white or black based on background color
function getContrastTextColor(hexColor: string): string {
  hexColor = hexColor.replace('#', '');

  const r = parseInt(hexColor.substring(0, 2), 16);
  const g = parseInt(hexColor.substring(2, 4), 16);
  const b = parseInt(hexColor.substring(4, 6), 16);

  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  return brightness > 128 ? '#000000' : '#ffffff';
}

const CustomDragLayer: React.FC = () => {
  const {
    itemType,
    isDragging,
    item,
    currentOffset,
  } = useDragLayer((monitor) => ({
    item: monitor.getItem() as ElementType,
    itemType: monitor.getItemType(),
    currentOffset: monitor.getClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  if (!isDragging || itemType !== 'element' || !currentOffset) {
    return null;
  }

  // Use the element's color if available, otherwise fall back to the default color based on type
  const elementColor = item.color || getDefaultColorByType(item.type);

  // Position element at mouse coordinates with center offset
  const style: React.CSSProperties = {
    position: 'fixed',
    pointerEvents: 'none',
    zIndex: 100,
    left: 0,
    top: 0,
    transform: `translate(${currentOffset.x}px, ${currentOffset.y}px) translate(-50%, -50%)`,
    WebkitTransform: `translate(${currentOffset.x}px, ${currentOffset.y}px) translate(-50%, -50%)`,
  };

  return (
    <div style={{ height: '100%', width: '100%', position: 'fixed', pointerEvents: 'none', zIndex: 100, left: 0, top: 0 }}>
      <div style={style}>
        <div
          style={{
            backgroundColor: elementColor,
            color: getContrastTextColor(elementColor),
            padding: '10px 16px',
            borderRadius: '0.75rem',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            fontSize: '14px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1.4,
          }}
        >
          {item.name}
        </div>
      </div>
    </div>
  );
};

export default CustomDragLayer;